import type {
  NationCapacity,
  ResourceStockpile,
} from '../core/economyTypes'
import { getNationDefinition } from './nations'
import type { MajorPowerId, NationId } from './nations'
export { isMajorPowerId, isActiveMajorPowerId } from './nations'
export type { MajorPowerId, ActiveMajorPowerId } from './nations'

export type RegionOwnerId = NationId

export type RegionRole = 'major-core' | 'client-state'
export type ResourceFocus = 'metal' | 'fuel' | 'electronics' | 'balanced'

export interface RegionDefinition {
  id: string
  name: string
  ownerId: RegionOwnerId
  ownerName: string
  patronId: MajorPowerId
  role: RegionRole
  terrain: string
  resourceFocus: ResourceFocus
  resourceLabel: string
  slots: number
  production: ResourceStockpile
  capacity: NationCapacity
  x: number
  y: number
  width: number
  height: number
  neighbors: string[]
}

export const PROTOTYPE_REGIONS: RegionDefinition[] = [
  {
    id: 'star-west',
    name: '西海岸工业区',
    ownerId: 'starsea',
    ownerName: '阿美丽卡联邦',
    patronId: 'starsea',
    role: 'major-core',
    terrain: '沿海工业带',
    resourceFocus: 'electronics',
    resourceLabel: '电子工业',
    slots: 4,
    production: { money: 8, metal: 2, fuel: 1, electronics: 5 },
    capacity: { power: 2, research: 2 },
    x: 45, y: 80, width: 165, height: 105,
    neighbors: ['star-central', 'southcape'],
  },
  {
    id: 'star-central',
    name: '中央平原',
    ownerId: 'starsea',
    ownerName: '阿美丽卡联邦',
    patronId: 'starsea',
    role: 'major-core',
    terrain: '平原',
    resourceFocus: 'balanced',
    resourceLabel: '综合工业',
    slots: 4,
    production: { money: 8, metal: 4, fuel: 3, electronics: 2 },
    capacity: { power: 3, research: 1 },
    x: 225, y: 95, width: 165, height: 105,
    neighbors: ['star-west', 'star-south', 'star-east'],
  },
  {
    id: 'star-south',
    name: '南湾能源带',
    ownerId: 'starsea',
    ownerName: '阿美丽卡联邦',
    patronId: 'starsea',
    role: 'major-core',
    terrain: '海湾与低地',
    resourceFocus: 'fuel',
    resourceLabel: '燃料',
    slots: 4,
    production: { money: 7, metal: 2, fuel: 6, electronics: 1 },
    capacity: { power: 3, research: 1 },
    x: 155, y: 225, width: 170, height: 105,
    neighbors: ['star-central', 'star-east', 'southcape'],
  },
  {
    id: 'star-east',
    name: '东部科研区',
    ownerId: 'starsea',
    ownerName: '阿美丽卡联邦',
    patronId: 'starsea',
    role: 'major-core',
    terrain: '沿海城市群',
    resourceFocus: 'electronics',
    resourceLabel: '科研与电子',
    slots: 4,
    production: { money: 9, metal: 1, fuel: 1, electronics: 6 },
    capacity: { power: 2, research: 3 },
    x: 340, y: 215, width: 165, height: 105,
    neighbors: ['star-central', 'star-south', 'isles'],
  },
  {
    id: 'isles',
    name: '群岛共和国',
    ownerId: 'isles-republic',
    ownerName: '群岛共和国',
    patronId: 'starsea',
    role: 'client-state',
    terrain: '群岛',
    resourceFocus: 'electronics',
    resourceLabel: '电子元件',
    slots: 2,
    production: { money: 4, metal: 0, fuel: 1, electronics: 3 },
    capacity: { power: 1, research: 1 },
    x: 430, y: 55, width: 120, height: 85,
    neighbors: ['star-east', 'northsea'],
  },
  {
    id: 'southcape',
    name: '南岬共同体',
    ownerId: 'southcape-commonwealth',
    ownerName: '南岬共同体',
    patronId: 'starsea',
    role: 'client-state',
    terrain: '半岛',
    resourceFocus: 'fuel',
    resourceLabel: '燃料',
    slots: 2,
    production: { money: 4, metal: 1, fuel: 4, electronics: 0 },
    capacity: { power: 2, research: 0 },
    x: 285, y: 365, width: 125, height: 85,
    neighbors: ['star-west', 'star-south', 'steppe'],
  },
  {
    id: 'northsea',
    name: '北海人民共和国',
    ownerId: 'northsea-republic',
    ownerName: '北海人民共和国',
    patronId: 'redplain',
    role: 'client-state',
    terrain: '寒冷海岸',
    resourceFocus: 'metal',
    resourceLabel: '金属',
    slots: 2,
    production: { money: 4, metal: 3, fuel: 1, electronics: 0 },
    capacity: { power: 2, research: 0 },
    x: 565, y: 55, width: 125, height: 85,
    neighbors: ['isles', 'red-west', 'red-north'],
  },
  {
    id: 'steppe',
    name: '草原联盟国',
    ownerId: 'steppe-union',
    ownerName: '草原联盟国',
    patronId: 'redplain',
    role: 'client-state',
    terrain: '草原',
    resourceFocus: 'fuel',
    resourceLabel: '燃料',
    slots: 2,
    production: { money: 4, metal: 1, fuel: 4, electronics: 0 },
    capacity: { power: 2, research: 0 },
    x: 545, y: 365, width: 125, height: 85,
    neighbors: ['southcape', 'red-central', 'red-east'],
  },
  {
    id: 'red-west',
    name: '西部工业带',
    ownerId: 'redplain',
    ownerName: '苏维埃联盟',
    patronId: 'redplain',
    role: 'major-core',
    terrain: '城市与平原',
    resourceFocus: 'balanced',
    resourceLabel: '综合工业',
    slots: 4,
    production: { money: 8, metal: 4, fuel: 2, electronics: 2 },
    capacity: { power: 3, research: 1 },
    x: 575, y: 160, width: 165, height: 105,
    neighbors: ['northsea', 'red-central', 'red-north'],
  },
  {
    id: 'red-central',
    name: '中央矿产区',
    ownerId: 'redplain',
    ownerName: '苏维埃联盟',
    patronId: 'redplain',
    role: 'major-core',
    terrain: '山地与高原',
    resourceFocus: 'metal',
    resourceLabel: '金属',
    slots: 4,
    production: { money: 7, metal: 6, fuel: 2, electronics: 1 },
    capacity: { power: 3, research: 1 },
    x: 690, y: 275, width: 165, height: 105,
    neighbors: ['red-west', 'red-north', 'red-east', 'steppe'],
  },
  {
    id: 'red-north',
    name: '北方科研城',
    ownerId: 'redplain',
    ownerName: '苏维埃联盟',
    patronId: 'redplain',
    role: 'major-core',
    terrain: '寒冷平原',
    resourceFocus: 'electronics',
    resourceLabel: '科研与电子',
    slots: 4,
    production: { money: 8, metal: 2, fuel: 1, electronics: 5 },
    capacity: { power: 2, research: 3 },
    x: 750, y: 75, width: 165, height: 105,
    neighbors: ['northsea', 'red-west', 'red-central', 'red-east'],
  },
  {
    id: 'red-east',
    name: '东境港区',
    ownerId: 'redplain',
    ownerName: '苏维埃联盟',
    patronId: 'redplain',
    role: 'major-core',
    terrain: '远东沿海',
    resourceFocus: 'fuel',
    resourceLabel: '燃料与港口',
    slots: 4,
    production: { money: 8, metal: 2, fuel: 5, electronics: 2 },
    capacity: { power: 3, research: 1 },
    x: 790, y: 405, width: 150, height: 85,
    neighbors: ['red-central', 'red-north', 'steppe'],
  },
]

export function getPrototypeRegion(regionId: string): RegionDefinition | undefined {
  return PROTOTYPE_REGIONS.find((region) => region.id === regionId)
}

export function getPatronName(patronId: MajorPowerId): string {
  return getNationDefinition(patronId).displayName
}
